import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Heart, Plus, Loader2, ExternalLink } from "lucide-react";

const TYPES = ["All", "Survivor", "Video Idea", "Build", "Challenge", "Clip", "Creator Tip"];
const POST_TYPES = ["Video Idea", "Build", "Challenge", "Clip", "Creator Tip"];

const typeColor = (t) => ({
  "Survivor": "text-primary border-primary/40",
  "Video Idea": "text-blue-400 border-blue-400/40",
  "Build": "text-purple-400 border-purple-400/40",
  "Challenge": "text-orange-400 border-orange-400/40",
  "Clip": "text-green-400 border-green-400/40",
  "Creator Tip": "text-primary border-primary/40",
}[t] || "text-foreground/70 border-white/20");

export default function Community() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("All");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ type: "Video Idea", title: "", content: "", link: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts", { params: { type: f } });
      setPosts(data);
    } catch { toast.error("Could not load posts."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/community/posts", form);
      toast.success("Posted to the community!");
      setCreateOpen(false);
      setForm({ type: "Video Idea", title: "", content: "", link: "" });
      load("All"); setFilter("All");
    } catch { toast.error("Could not post."); }
    finally { setSubmitting(false); }
  };

  const like = async (id) => {
    if (!user) return setAuthOpen(true);
    try {
      const { data } = await api.post(`/community/posts/${id}/like`);
      setPosts((ps) => ps.map((p) => p.id === id ? { ...p, likes: data.likes, liked_by: data.liked ? [...(p.liked_by || []), user.user_id] : (p.liked_by || []).filter((u) => u !== user.user_id) } : p));
    } catch { toast.error("Could not like."); }
  };

  return (
    <div data-testid="community-page" className="mx-auto max-w-6xl px-6 py-14 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Users className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.25em]">Community</span>
          </div>
          <h1 className="font-display text-5xl tracking-wider md:text-6xl">The <span className="text-primary">Campfire</span></h1>
          <p className="mt-2 text-foreground/60">Share ideas, builds, clips and tips with fellow creators.</p>
        </div>

        <Dialog open={createOpen} onOpenChange={(o) => user ? setCreateOpen(o) : setAuthOpen(true)}>
          <DialogTrigger asChild>
            <Button data-testid="new-post-btn" onClick={(e) => { if (!user) { e.preventDefault(); setAuthOpen(true); } }} className="rounded-sm bg-primary font-bold uppercase tracking-widest hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-sm border-white/10 bg-card">
            <DialogHeader><DialogTitle className="font-display text-3xl tracking-wider">Share with the fog</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-foreground/70">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="post-type-select" className="rounded-sm border-white/10 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-card">{POST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-foreground/70">Title</Label>
                <Input data-testid="post-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm border-white/10 bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-foreground/70">Content</Label>
                <Textarea data-testid="post-content-input" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="min-h-28 rounded-sm border-white/10 bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-foreground/70">Link (optional)</Label>
                <Input data-testid="post-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://youtube.com/..." className="rounded-sm border-white/10 bg-background" />
              </div>
              <Button data-testid="post-submit-btn" type="submit" disabled={submitting} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 flex flex-wrap gap-2.5">
        {TYPES.map((t) => (
          <button key={t} data-testid={`filter-${t.replace(/\s/g, "-")}`} onClick={() => setFilter(t)} className={`border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${filter === t ? "border-primary bg-primary text-primary-foreground" : "border-white/10 text-foreground/70 hover:border-primary/50"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-8 grid gap-4">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-white/10 py-20 text-center text-foreground/50">No posts yet. Be the first to share.</div>
        ) : posts.map((p) => {
          const liked = user && (p.liked_by || []).includes(user.user_id);
          return (
            <div key={p.id} data-testid="community-post" className="group border border-border bg-card p-6 transition-all hover:border-primary/40">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${typeColor(p.type)}`}>{p.type}</span>
                    <Link to={`/u/${p.username}`} className="text-xs font-bold uppercase tracking-widest text-foreground/60 hover:text-primary">@{p.username}</Link>
                  </div>
                  <h3 className="mt-3 font-display text-2xl tracking-wide">{p.title}</h3>
                  {p.image_path && (
                    <img
                      data-testid="post-image"
                      src={`${API}/files/${p.image_path}`}
                      alt={p.title}
                      loading="lazy"
                      className="mt-3 max-h-96 w-full max-w-sm border border-white/10 object-cover"
                    />
                  )}
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/70">{p.content}</p>
                  {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary hover:underline">View link <ExternalLink className="h-3.5 w-3.5" /></a>}
                </div>
              </div>
              <div className="mt-4 border-t border-white/10 pt-3">
                <button data-testid={`like-btn-${p.id}`} onClick={() => like(p.id)} className={`flex items-center gap-2 text-sm font-bold transition-colors ${liked ? "text-primary" : "text-foreground/50 hover:text-primary"}`}>
                  <Heart className={`h-4 w-4 ${liked ? "fill-primary" : ""}`} /> {p.likes}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
